/**
 * galleryStore.ts
 * Persists uploaded gallery photos in IndexedDB (no size limit).
 * Falls back to localStorage for small metadata.
 *
 * HERO IMAGE STRATEGY:
 * - localStorage stores a tiny reference string (e.g. "indexeddb://upload-123" or a raw URL)
 * - If the reference starts with "indexeddb://", we look up the actual image in IndexedDB
 * - This avoids localStorage quota issues with large base64 data URLs
 */

import type { GalleryPhoto } from "@/data/siteContent";

const DB_NAME = "ma_gallery_db";
const DB_VERSION = 1;
const STORE_NAME = "uploaded_photos";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function loadUploadedPhotosAsync(): Promise<GalleryPhoto[]> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result as GalleryPhoto[]);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

export async function saveUploadedPhotoAsync(photo: GalleryPhoto): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(photo);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // ignore
  }
}

export async function deleteUploadedPhotoAsync(id: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // ignore
  }
}

export async function saveAllUploadedPhotosAsync(photos: GalleryPhoto[]): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      // Clear all then re-add
      const clearReq = store.clear();
      clearReq.onsuccess = () => {
        let pending = photos.length;
        if (pending === 0) { resolve(); return; }
        photos.forEach((p) => {
          const putReq = store.put(p);
          putReq.onsuccess = () => { pending--; if (pending === 0) resolve(); };
          putReq.onerror = () => reject(putReq.error);
        });
      };
      clearReq.onerror = () => reject(clearReq.error);
    });
  } catch {
    // ignore
  }
}

// ─── Hero image helpers ───────────────────────────────────────────────────────

const HERO_REF_KEY = "ma_hero_bg_ref";

/**
 * Store a hero image reference in localStorage.
 * For uploaded photos (base64), store "indexeddb://<photoId>".
 * For external URLs, store the URL directly.
 */
export function storeHeroRef(ref: string): void {
  try {
    localStorage.setItem(HERO_REF_KEY, ref);
  } catch {
    // ignore
  }
  // Stage for Supabase sync
  try {
    import("@/hooks/useAdminStorage").then(({ stagingStoreSet, stagingStoreNotify }) => {
      stagingStoreSet(HERO_REF_KEY, ref);
      stagingStoreNotify(HERO_REF_KEY);
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

/**
 * Get the raw hero image reference from localStorage.
 */
export function getHeroRef(): string | null {
  try {
    return localStorage.getItem(HERO_REF_KEY);
  } catch {
    return null;
  }
}

/**
 * Resolve a hero image reference to an actual URL.
 * If the ref is "indexeddb://<id>", look up the photo in IndexedDB.
 * Otherwise return the ref as-is (it's an external URL).
 */
export async function resolveHeroImageAsync(ref: string | null): Promise<string | null> {
  if (!ref) return null;
  if (ref.startsWith("indexeddb://")) {
    const id = ref.replace("indexeddb://", "");
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(id);
        req.onsuccess = () => {
          const photo = req.result as GalleryPhoto | undefined;
          resolve(photo?.url ?? null);
        };
        req.onerror = () => reject(req.error);
      });
    } catch {
      return null;
    }
  }
  return ref;
}

/**
 * Build a reference string for a photo.
 * If it's an uploaded photo (has isUploaded flag), use indexeddb:// ref.
 * Otherwise use the raw URL.
 */
export function buildHeroRef(photo: GalleryPhoto): string {
  if (photo.isUploaded) {
    return `indexeddb://${photo.id}`;
  }
  return photo.url;
}
